/** パスワード変更コンポーネント */
<template>
  <div class="member-password-editor">
    <ifield-b-modal
      id="the-member-password-editor"
      v-bind:title="$t('theMemberPasswordEditor.changePassword')"
      centered
      scrollable
      no-body-border
      size="lg"
      ok-variant="success"
      ignore-enforce-focus-selector=".popover"
      v-bind:ok-title="$t('theMemberPasswordEditor.action.register')"
      v-bind:cancel-title="$t('theMemberPasswordEditor.action.cancel')"
      v-on:ok.prevent="$_savePassword"
    >
      <loading-mask isFullScreen v-bind:isLoading="isSaving" />
      <form-group id="member-password-form" ref="form-group" />
    </ifield-b-modal>
  </div>
</template>

<script>
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { RestApiClient } from 'ifield-core-components'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

export default {
  name: 'TheMemberPasswordEditor',
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      /**
       * 対象のユーザー
       * @type {Object}
       */
      user: null,
      /**
       * 保存中フラグ
       * @type {Boolean}
       */
      isSaving: false,
    }
  },
  computed: {
    /**
     * フォーム項目
     * @type {Array.<Object>}
     */
    forms: function () {
      return [
        // パスワード
        {
          fieldName: 'password',
          label: this.$t('theMemberPasswordEditor.field.password'),
          attributeType: ATTRIBUTE_TYPE.PASSWORD,
          value: '',
          validateRule: {
            required: true,
            min: 8,
            max: 50,
            is_strong_password: {
              value: {
                threshold: 30,
                options: { pointsPerUnique: 0, pointsPerRepeat: 0 },
              },
              text: this.$t(
                'theMemberPasswordEditor.rule.characterTypeAtLeastThree'
              ),
            },
            regex: {
              value: new RegExp(
                `^(?!.*${this.$store.state.login.loginCode}).*$`
              ),
              text: this.$t(
                'theMemberPasswordEditor.rule.notContainsLoginCode'
              ),
            },
          },
        },
        // パスワード（確認）
        {
          fieldName: 'password_for_check',
          label: this.$t('theMemberPasswordEditor.field.passwordConfirm'),
          attributeType: ATTRIBUTE_TYPE.PASSWORD,
          value: '',
          validateRule: {
            required: true,
            confirmed: {
              value: 'password',
              text: this.$t('theMemberPasswordEditor.rule.equalTo', {
                value: this.$t('theMemberPasswordEditor.field.password'),
              }),
            },
          },
        },
      ]
    },
    /**
     * ユーザーデータ
     * @type {Object}
     */
    requestUserData: function () {
      if (!this.user) return {}

      const {
        attributes,
        groups,
        email,
        profile_image,
        remarks,
        tel,
        user_name,
        user_name_kana,
      } = this.user
      return {
        attributes: attributes.map(({ attribute_id, attribute_value }) => ({
          attribute_id,
          attribute_value,
        })),
        // 所属グループは編集不可であるが、APIの仕様上groupsを送らないとnullに設定されてしまうため、グループの場合はorganization_idの配列
        groups: groups.map((value) => ({
          organization_id: value.id,
        })),
        email,
        profile_image,
        remarks,
        tel,
        user_name,
        user_name_kana,
      }
    },
  },
  mounted: function () {
    this.$log.info('memberPasswordEditor mounted!')
  },
  methods: {
    /**
     * パスワード編集モーダルを表示する
     * @public
     */
    showModal: function (user) {
      this.$log.info('memberPasswordEditor showModal')
      if (!user || user.id !== this.$store.getters.loginState.userId) {
        this.$log.error('cannot open the modal window. (invalid user)', user)
        return
      }

      this.user = user

      // ローディング表示
      this.$_showLoading()

      this.$bvModal.show('the-member-password-editor')
      this.$nextTick().then(() => {
        this.$_setData()
      })

      // ローディング終了
      this.$_hideLoading()
    },
    /**
     * パスワード編集モーダルを閉じる
     * @public
     */
    hideModal: function () {
      this.$bvModal.hide('the-member-password-editor')
    },

    /**
     * フォームに設定する
     */
    $_setData: function () {
      const form = this.$refs['form-group']
      form && form.setData(this.forms)
    },

    /**
     * パスワードを変更する
     */
    $_savePassword: async function () {
      // フォームグループの値を取得する
      let formGroupValues
      try {
        formGroupValues = this.$refs['form-group'].getValues()
      } catch (error) {
        this.$log.error(error)
        return
      }

      this.$_showLoading()

      // フォームグループの値をリクエストbodyに代入
      const requestBody = { ...this.requestUserData }
      formGroupValues.forEach((item) => {
        requestBody[item.name] = item.value
      })

      // APIを実行する
      const url = RestApiClient.createApiUrl(`user/myaccount`)
      try {
        // 更新する
        await RestApiClient.executePutApi(url, requestBody)

        this.$_showSaveSuccessMessage()

        this.$emit('member-password-edit-end')

        // モーダルを閉じる
        this.hideModal()
      } catch (error) {
        this.handleApiError(error)
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * パスワード変更が成功したメッセージを表示する
     */
    $_showSaveSuccessMessage: function () {
      this.$toast.fire({
        position: 'top',
        icon: 'success',
        title: this.$t('theMemberPasswordEditor.message.changed'),
        showConfirmButton: true,
        confirmButtonColor: '#6c757d',
        timer: 2000,
      })
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isSaving = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isSaving = false
    },
  },
}
</script>
